import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import { NewInquiry } from "@/components/NewInquiry";
import { ChatView } from "@/components/ChatView";
import GalleryPage from "@/pages/GalleryPage";
import ComparePage from "@/pages/ComparePage";
import StudioPage from "@/pages/StudioPage";
import AdminPage from "@/pages/AdminPage";
import { useAuth } from "@/context/AuthContext";
import { VideoJobsProvider } from "@/context/VideoJobsContext";

export default function Workspace({ view }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data)).catch((err) => toast.error(formatApiError(err)));
    loadConversations();
  }, [loadConversations]);

  const startSession = async ({ category, provider, model, seed_messages }) => {
    try {
      const { data } = await api.post("/conversations", { category, provider, model, seed_messages });
      setConversations((prev) => [data, ...prev]);
      navigate(`/c/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteConversation = async (convId) => {
    try {
      await api.delete(`/conversations/${convId}`);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      toast.success("Thread deleted");
      if (convId === id) navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const patchConversation = (updated) =>
    setConversations((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)).sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1)));

  return (
    <VideoJobsProvider>
    <div className="h-full flex bg-[#090A0F] text-slate-300 overflow-hidden" data-testid="workspace">
      <Sidebar
        conversations={conversations}
        activeId={id}
        catalog={catalog}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onDelete={deleteConversation}
      />
      <main className="flex-1 min-w-0 flex flex-col relative">
        {view === "gallery" ? (
          <GalleryPage catalog={catalog} onOpenSidebar={() => setSidebarOpen(true)} />
        ) : view === "compare" ? (
          <ComparePage catalog={catalog} onStart={startSession} onOpenSidebar={() => setSidebarOpen(true)} />
        ) : view === "studio" ? (
          <StudioPage onOpenSidebar={() => setSidebarOpen(true)} />
        ) : view === "admin" ? (
          user?.role === "admin" ? <AdminPage onOpenSidebar={() => setSidebarOpen(true)} /> : <NewInquiry catalog={catalog} onStart={startSession} onOpenSidebar={() => setSidebarOpen(true)} />
        ) : id ? (
          <ChatView key={id} convId={id} catalog={catalog} onConversationChange={patchConversation} onOpenSidebar={() => setSidebarOpen(true)} />
        ) : (
          <NewInquiry catalog={catalog} onStart={startSession} onOpenSidebar={() => setSidebarOpen(true)} />
        )}
      </main>
    </div>
    </VideoJobsProvider>
  );
}
